import { createSlice } from '@reduxjs/toolkit';

import SDK from '../Components/SDK';
import logger from '../Helpers/logger';
import { clearState } from './clearSlice';

const MAX_IDS_PER_REQUEST = 200;
// Bubbles mount one by one, so their requests are gathered into one call.
const BATCH_DELAY_MS = 50;

// userId -> public user name, or null when the user has none.
const initialState = {
    byUserId: {},
};

const publicUserNameSlice = createSlice({
    name: 'publicUserName',
    initialState,
    reducers: {
        setPublicUserNames(state, action) {
            state.byUserId = { ...state.byUserId, ...action.payload };
        },
    },
    extraReducers: builder => {
        builder.addCase(clearState, () => initialState);
    },
});

export const { setPublicUserNames } = publicUserNameSlice.actions;

/**
 * Public user name of a user, shown as @username in mentions (FR103).
 *
 * @param {object} state Redux state.
 * @param {string} userId User id.
 * @returns {string} The name, or '' when the user has none or it is not loaded yet.
 */
export const selectPublicUserName = (state, userId) => state.publicUserName.byUserId[userId] || '';

// Users waiting for the next batch, and users whose request is on its way.
const pendingUserIds = new Set();
const inFlightUserIds = new Set();
let batchTimer = null;

/**
 * Loads the public user names of users not looked up yet, in batches of at most 200.
 *
 * @param {string[]} userIds Users to look up.
 */
export const requestPublicUserNames = userIds => (dispatch, getState) => {
    const { byUserId } = getState().publicUserName;
    (userIds || []).filter(Boolean).forEach(userId => {
        if (byUserId[userId] === undefined && !inFlightUserIds.has(userId)) pendingUserIds.add(userId);
    });
    if (!pendingUserIds.size || batchTimer) return;
    batchTimer = setTimeout(() => {
        batchTimer = null;
        const batch = [...pendingUserIds];
        pendingUserIds.clear();
        for (let start = 0; start < batch.length; start += MAX_IDS_PER_REQUEST) {
            dispatch(fetchBatch(batch.slice(start, start + MAX_IDS_PER_REQUEST)));
        }
    }, BATCH_DELAY_MS);
};

const fetchBatch = userIds => async dispatch => {
    userIds.forEach(userId => inFlightUserIds.add(userId));
    const response = await SDK.getPublicUserNames(userIds).catch(error => ({ statusCode: 0, error }));
    userIds.forEach(userId => inFlightUserIds.delete(userId));
    if (response?.statusCode !== 200) {
        // Asked again on the next request; until then the display name is shown.
        logger.warn('Public user names not loaded', { count: userIds.length, statusCode: response?.statusCode });
        return;
    }
    const names = Object.fromEntries(userIds.map(userId => [userId, null]));
    (response.data || []).forEach(({ userId, publicUserName }) => {
        names[userId] = publicUserName || null;
    });
    dispatch(setPublicUserNames(names));
};

export default publicUserNameSlice.reducer;
